import { EntryType, type TreeNode } from '@morph-mapper/types';
import { definePlugin } from '../../framework';
import type { FieldSuggestion, IteratorStrategy, UtilsApi } from '../../types';
import { keepScoredSuggestions, scoreSuggestion } from '../sort-suggestions';
import { isNumericIntentField } from '../number-handling';

const SIBLING_DELIMITERS = ['.', ',', '/'];
const MAX_SEGMENTS = 6;

const isWrappedSnippet = (snippet: unknown): snippet is [unknown[]] =>
  Array.isArray(snippet) &&
  Array.isArray(snippet[0]) &&
  snippet[0].length > 0;

export function buildSplitSnippet(originalSnippet: unknown, delimiter: string, index: number) {
  return {
    $declareVariable: {
      original: originalSnippet,
      part: {
        '$original.split': delimiter,
        $result: { $getVariable: `data.${index}` },
      },
    },
    $getVariable: 'part',
  };
}

function splitAllSegments(value: string, delimiter: string): string[] | null {
  const segments = value.split(delimiter).map((s) => s.trim());
  if (segments.length < 2 || segments.length > MAX_SEGMENTS) return null;
  if (segments.some((s) => s.length === 0)) return null;
  return segments;
}

function looksLikeFormattedNumber(value: string): boolean {
  const match = value.trim().match(/^([\d.,]+)\s*([a-zA-Z%€$£]{0,6})?$/);
  if (!match) return false;
  const numericPart = match[1];
  if (!/\d/.test(numericPart)) return false;
  if (/[.,]{2,}/.test(numericPart)) return false;
  if (/^[.,]|[.,]$/.test(numericPart)) return false;
  return true;
}

function looksLikeDateOrTime(value: string): boolean {
  const datePattern = /\b\d{1,4}[.\-/]\d{1,2}[.\-/]\d{1,4}\b/;
  const timePattern = /\b\d{1,2}:\d{2}(:\d{2})?\b/;
  return datePattern.test(value) || timePattern.test(value);
}

function looksLikeDateFormatPattern(value: string): boolean {
  const tokens = value.trim().split(/[^a-zA-Z]+/).filter(Boolean);
  if (tokens.length === 0) return false;
  return tokens.every((token) => /^([YMDHhms])\1*$/.test(token));
}

/** A code with both letters and digits, e.g. a container type like "4HDC" or "22G1". */
const CODE_TOKEN = /^(?=.*\d)(?=.*[A-Za-z])[A-Za-z0-9#'-]+$/;
const isPlainNumber = (s: string) => /^[\d.,]+$/.test(s) && looksLikeFormattedNumber(s);

type SplitPart = { index: number; part: string };

/**
 * A code and a number stuck together in one value, e.g. "4HDC 3,700"
 * (container type and tare): exactly two parts separated by whitespace, one
 * a plain number, the other a code with letters and digits. Values that are
 * just words ("ROTTERDAM NETHERLANDS", "Havennummer 8970") don't qualify.
 */
export function findCodeNumberPair(
  value: string,
): { delimiter: string; code: SplitPart; number: SplitPart } | null {
  const match = value.trim().match(/^(\S+)(\s+)(\S+)$/);
  if (!match) return null;
  const [, first, delimiter, second] = match;
  if (CODE_TOKEN.test(first) && isPlainNumber(second)) {
    return { delimiter, code: { index: 0, part: first }, number: { index: 1, part: second } };
  }
  if (isPlainNumber(first) && CODE_TOKEN.test(second)) {
    return { delimiter, code: { index: 1, part: second }, number: { index: 0, part: first } };
  }
  return null;
}

function dedupeByValue<T extends { value: unknown }>(items: T[]): T[] {
  const seen = new Set<string>();
  const result: T[] = [];
  for (const item of items) {
    const key = typeof item.value === 'string' ? item.value.trim() : JSON.stringify(item.value);
    if (seen.has(key)) continue;
    seen.add(key);
    result.push(item);
  }
  return result;
}

export type SplitCandidate = {
  delimiter: string;
  index: number;
  part: string;
  /**
   * Nothing else backs this part up, so it is offered without filling the
   * field and ranks below the full value.
   */
  unconfirmed?: true;
};

export function findSplitCandidates(
  suggestion: FieldSuggestion,
  others: FieldSuggestion[],
  numericField = false,
): SplitCandidate[] {
  if (suggestion.iterator === true) return [];
  if (typeof suggestion.value !== 'string') return [];
  if (
    looksLikeFormattedNumber(suggestion.value) ||
    looksLikeDateOrTime(suggestion.value) ||
    looksLikeDateFormatPattern(suggestion.value)
  ) {
    return [];
  }

  const snippet = suggestion.snippet;
  if (typeof snippet !== 'string' && !isWrappedSnippet(snippet)) return [];

  const colonSegments = splitAllSegments(suggestion.value, ':');
  if (colonSegments) {
    const lastIndex = colonSegments.length - 1;
    return [{ delimiter: ':', index: lastIndex, part: colonSegments[lastIndex] }];
  }

  const ownExpectedValue =
    typeof suggestion.expectedValue === 'string' ? suggestion.expectedValue.trim() : null;

  const isCorroborated = (segment: string) =>
    ownExpectedValue === segment ||
    others.some(
      (s) =>
        s !== suggestion &&
        ((typeof s.value === 'string' && s.value.trim() === segment) ||
          (typeof s.expectedValue === 'string' && s.expectedValue.trim() === segment)),
    );

  // A code stuck to a number: a numeric field gets the number, any other
  // field the code. The number is safe to fill in a numeric field. In a text
  // field the space may belong to the value ("INV-2024 0012"), so the code
  // only fills the field when another suggestion backs it up.
  const pair = findCodeNumberPair(suggestion.value);
  if (pair) {
    const { index, part } = numericField ? pair.number : pair.code;
    const candidate: SplitCandidate = { delimiter: pair.delimiter, index, part };
    if (!numericField && !isCorroborated(part)) candidate.unconfirmed = true;
    return [candidate];
  }

  const candidates: SplitCandidate[] = [];
  for (const delimiter of SIBLING_DELIMITERS) {
    const segments = splitAllSegments(suggestion.value, delimiter);
    if (!segments) continue;

    segments.forEach((part, index) => {
      if (isCorroborated(part)) candidates.push({ delimiter, index, part });
    });
  }
  return candidates;
}

const valueKey = (value: unknown) =>
  typeof value === 'string' ? value.trim() : JSON.stringify(value);

export async function buildSplitSuggestions(args: {
  entry: TreeNode;
  suggestions: FieldSuggestion[] | void;
  iteratorStrategy?: IteratorStrategy;
  callRender: UtilsApi['callRender'];
}) {
  const { entry, suggestions, iteratorStrategy, callRender } = args;
  if (!suggestions || suggestions.length === 0) return [];
  if (String(entry.key) === '#iterator') return [];

  const snapshot = dedupeByValue([...suggestions]);

  const scored = [];
  for (const suggestion of snapshot) {
    const { score } = await scoreSuggestion(suggestion, callRender);
    scored.push({
      suggestion,
      value: suggestion.value,
      score,
      crossField: (suggestion as any).crossField,
    });
  }

  const kept = keepScoredSuggestions(scored, entry, iteratorStrategy);
  const existingValues = new Set(kept.map((k) => valueKey(k.value)));

  const additions: any[] = [];
  for (const { suggestion, score } of kept) {
    const snippet = suggestion.snippet;
    const innerSnippet = isWrappedSnippet(snippet) ? (snippet as unknown[][])[0][0] : snippet;

    const candidates = findSplitCandidates(
      suggestion,
      snapshot,
      isNumericIntentField(entry),
    );
    for (const { delimiter, index, part, unconfirmed } of candidates) {
      if (existingValues.has(part)) continue;

      const finalSnippet = buildSplitSnippet(innerSnippet, delimiter, index);
      const renderResult = await callRender(JSON.stringify(finalSnippet));
      if (!renderResult.isOk()) continue;
      if (String(renderResult.value).trim() !== part) continue;

      additions.push({
        value: part,
        snippet: finalSnippet as any,
        type: EntryType.Code,
        autofill: !unconfirmed,
        score: unconfirmed ? score - 1 : score + 1,
        source: suggestion.source,
      });
    }
  }

  additions.sort((a, b) => b.score - a.score);
  return dedupeByValue(additions);
}

export const splitHandling = definePlugin<{}>({
  name: 'Split Handling',
  version: '0.5.0',
  description:
    'Adds additional suggestions that split a compound value (e.g. "carrier, vessel, voyage") into its individual parts on ".", ",", "/", or ":", and a code stuck to a number (e.g. "4HDC 3,700") into the part the field needs.',
  initialState: {},

  bootstrap: () => {},

  postFieldSuggestions({ entry, suggestions, iteratorStrategy, callRender }) {
    return buildSplitSuggestions({ entry, suggestions, iteratorStrategy, callRender });
  },
});
